import React, { useContext, useRef, useState } from 'react';
import { ErrorContext } from '../../services/auth/error.provider';
import { Era835Summary, PAYMENT_METHOD_LABELS, summarize835 } from '../../services/era-835/era-835';
import { ToastContext } from '../../services/toast/toast';
import { BaseButton } from '../base/button';
import { BaseSelect, ISelectOption } from '../base/select/select';
import { BatchFileUploader } from '../batch-document-upload/batch-file-uploader';
import { renderAmount } from '../data-table/components/cells/cells';
import { formatDate } from '../../services/primitives/date-utils';
import * as S from './era-upload-dialog.styles';

export interface IEraUploadDialogProps {
    /** Shown in the intro; the id lives in `upload`, which already knows where to send the file. */
    clearinghouseName?: string;
    /**
     * Sends the file's text to the app's own endpoint — `postProviderX12835Era` in the provider app,
     * `postX12835Era` in support. Each app keeps its own store call; only the dialog is shared.
     */
    upload: (x12: string, clearinghouseId?: string) => Promise<unknown>;
    /**
     * The clearinghouses to choose from, when the caller has not chosen one (the Remits page, FE-163). Given, the
     * dialog asks which first and passes the choice to `upload`; omitted, it uploads where `upload` already points.
     */
    clearinghouses?: Array<{ id: string; name: string; type?: string; created?: string }>;
    /** Reports a failed upload. Defaults to the core ErrorContext, which support mounts. */
    onError?: (err: Error) => void;
    onClose: () => void;
}

interface ISelectedFile {
    file: File;
    content: string;
    summary: Era835Summary;
}

/**
 * Uploads an X12 835 to one clearinghouse — `POST /{provider|support}/clearinghouse/{id}/era/x12/5010/835`,
 * which creates an ERA from the file and answers with its id.
 *
 * The file is checked in the browser before the button enables (see era-835.ts), because the
 * endpoint will create an ERA from whatever it is sent. Two things this cannot check, and which the
 * operator is told about instead: whether this remittance was already uploaded — the request has no
 * idempotency key, so a second upload may make a second ERA — and anything past the payment
 * header, which is patient data and is not read here.
 */
export const EraUploadDialog: React.FC<IEraUploadDialogProps> = ({ clearinghouseName, upload, clearinghouses, onError, onClose }) => {
    const errorContext = useContext(ErrorContext);
    const toastContext = useContext(ToastContext);
    /** What the picker shows — set at once, before the file has been read. */
    const [file, setFile] = useState<File>();
    /** The same file once read and checked; lags `file` by one read. */
    const [selected, setSelected] = useState<ISelectedFile>();
    const [readError, setReadError] = useState<string>();
    const [uploading, setUploading] = useState(false);
    const [clearinghouseId, setClearinghouseId] = useState<string>();
    const picked = clearinghouses?.find(clearinghouse => clearinghouse.id === clearinghouseId);
    const destination = clearinghouses ? picked?.name : clearinghouseName;
    /** The file whose read should win — a slower read of an earlier pick must not overwrite it. */
    const latestFile = useRef<File | undefined>(undefined);
    /** A ref, not state: two clicks inside one render would both see `uploading === false`. */
    const inFlight = useRef(false);

    /** `files` is [] after Remove / Clear all, and at most one file otherwise (maxFiles={1}). */
    const handleFilesChange = async (files: File[]): Promise<void> => {
        const next = files[0];
        latestFile.current = next;
        setFile(next);
        setSelected(undefined);
        setReadError(undefined);
        if (!next) {
            return;
        }
        try {
            const content = await next.text();
            if (latestFile.current === next) {
                setSelected({ file: next, content, summary: summarize835(content) });
            }
        } catch {
            if (latestFile.current === next) {
                setReadError('The file could not be read.');
            }
        }
    };

    const handleUpload = async (): Promise<void> => {
        if (inFlight.current || !selected || selected.summary.error || (clearinghouses && !clearinghouseId)) {
            return;
        }
        inFlight.current = true;
        setUploading(true);
        try {
            await upload(selected.content, clearinghouseId);
            toastContext.notify?.(`ERA uploaded to ${destination || 'the clearinghouse'}.`, 'left');
            onClose();
        } catch (err) {
            // The dialog stays open with the same file, so the operator can read the error and decide.
            (onError || errorContext.handleError)(err as Error);
        } finally {
            inFlight.current = false;
            setUploading(false);
        }
    };

    const summary = selected?.summary;
    const uploadable = !!summary && !summary.error && (!clearinghouses || !!clearinghouseId);
    const clearinghouseOptions: ISelectOption<string>[] = (clearinghouses || []).map(clearinghouse => ({
        value: clearinghouse.id,
        // The core select's option draws `label` itself, so the open list gets two lines here (design 06):
        // the name over `WAYSTAR · connected Mar 2, 2025`. The chosen value shows the name alone (below).
        label: (
            <S.Option>
                <span>{clearinghouse.name || clearinghouse.id}</span>
                <S.OptionInfo>
                    {[clearinghouse.type, clearinghouse.created && `connected ${formatDate(clearinghouse.created, 'MMM d, yyyy')}`]
                        .filter(Boolean)
                        .join(' \u00b7 ')}
                </S.OptionInfo>
            </S.Option>
        ),
    }));
    const nameOf = (id: string | undefined): string =>
        clearinghouses?.find(clearinghouse => clearinghouse.id === id)?.name || id || '';
    const payments = summary?.payments || [];
    const errorMessage = readError || summary?.error;
    const recognised = uploadable
        ? `X12 835 · ${summary?.version} · ${payments.length} payment${payments.length === 1 ? '' : 's'}`
        : undefined;

    return (
        <S.DialogBody>
            {clearinghouses ? (
                <S.Field>
                    <S.FieldLabel>Clearinghouse</S.FieldLabel>
                    <BaseSelect
                        aria-label="Clearinghouse"
                        placeholder="Select clearinghouse"
                        options={clearinghouseOptions}
                        isSearchable={false}
                        formatOptionLabel={(option: unknown, meta: { context: string }): React.ReactNode =>
                            meta.context === 'value'
                                ? nameOf((option as ISelectOption<string>).value)
                                : (option as ISelectOption<string>).label
                        }
                        value={clearinghouseOptions.find(option => option.value === clearinghouseId) || null}
                        isDisabled={uploading}
                        onChange={(option: unknown): void =>
                            setClearinghouseId((option as ISelectOption<string> | null)?.value || undefined)
                        }
                    />
                </S.Field>
            ) : (
                <S.Intro>
                    Creates an ERA in <strong>{clearinghouseName || 'this clearinghouse'}</strong> from an X12 835
                    remittance file (version 5010).
                </S.Intro>
            )}
            {/* The provider's document-upload picker, capped at one file. `accept=""` turns its type
                filter off — its default is document types, and 835s arrive as .835, .txt, .edi, .x12,
                .era, .rmt and more. The content check decides, not the extension. */}
            {clearinghouses && <S.FieldLabel>835 file</S.FieldLabel>}
            <BatchFileUploader
                files={file ? [file] : []}
                onChange={handleFilesChange}
                accept=""
                maxFiles={1}
                disabled={uploading}
                errorMessage={errorMessage}
            />
            {recognised && <S.Recognised>{recognised}</S.Recognised>}
            {!!payments.length && (
                <S.Payments>
                    <S.PaymentHeader>
                        <span>Payer</span>
                        <span>Amount</span>
                        <span>Method</span>
                        <span>Payment date</span>
                        <span>Check / EFT number</span>
                    </S.PaymentHeader>
                    {payments.map((payment, index) => (
                        <S.Payment key={`${payment.traceNumber}-${index}`}>
                            <span>{payment.payerName || '-'}</span>
                            <S.Mono>
                                {payment.paymentAmount ? renderAmount({ amount: payment.paymentAmount }) : '-'}
                            </S.Mono>
                            <span>
                                {payment.paymentMethod
                                    ? PAYMENT_METHOD_LABELS[payment.paymentMethod] || payment.paymentMethod
                                    : '-'}
                            </span>
                            <S.Mono>{payment.paymentDate || '-'}</S.Mono>
                            <S.Mono>{payment.traceNumber || '-'}</S.Mono>
                        </S.Payment>
                    ))}
                </S.Payments>
            )}
            {!!payments.length && (
                <S.Caution>
                    Uploading the same file twice may create a duplicate ERA. Check that{' '}
                    {payments.length === 1 ? 'this check / EFT number has' : 'these check / EFT numbers have'} not
                    already been uploaded.
                </S.Caution>
            )}
            {clearinghouses && !clearinghouseId && <S.Recognised>Select a clearinghouse to upload.</S.Recognised>}
            <S.ButtonGroup>
                <BaseButton variant="outlined" size="medium" onClick={onClose} disabled={uploading}>
                    Cancel
                </BaseButton>
                <BaseButton variant="primary" size="medium" onClick={handleUpload} disabled={!uploadable || uploading}>
                    {uploading ? 'Uploading…' : 'Upload'}
                </BaseButton>
            </S.ButtonGroup>
        </S.DialogBody>
    );
};
